/**
 * The GROUND the strip map draws under the marks, decided without a canvas (08 §2.7).
 *
 * The ROUND5 audit found four things the briefings talk about and the fixed layer never drew: the
 * river with its fords and its cracked ice, the objective's own zones (the amber rect is the enemy
 * compound, so on `prison-break` the one "go here" glyph pointed at the camp he was escaping FROM),
 * the marked minefields, and the circles six archetypes lose the mission inside. ROUND5 §2.6 added
 * the pipe and the supports under it. The intel round added two more, both bought knowledge rather
 * than ground truth: the buildings (Recon Photos) and the sentries' rounds (Patrol Schedule).
 *
 * All of it is static - it comes off `MissionDef` and nothing in the world can change it - so it is
 * painted once, by `mapBackdrop.ts`. The marks that move live in `mapMarks.ts`.
 */

import { collidersFor, PROPS } from '../../sim/props';
import { HAZARDS } from '../../sim/tuning';
import type { MissionDef, Polyline, PropDef, PropKind, Rect } from '../../sim/types';

/** A circle on the map, in metres. */
export interface MapCircle {
  x: number;
  z: number;
  r: number;
}

/** A water barrier as the map draws it: the band, the lanes across it, the ice that gives way. */
export interface WaterMark {
  course: Polyline;
  surface: 'water' | 'ice';
  /** Fords, or thick ice: `WaterBarrierDef.crossings` are x-positions, so the z comes from the course. */
  crossings: Array<{ x: number; z: number; halfWidth: number }>;
  weakIce: Array<{ x: number; z: number; r: number }>;
}

/** A pipeline across the map: the steel, and the gaps under it a man can crawl through. */
export interface PipeMark {
  z: number;
  runs: Array<[x0: number, x1: number]>;
  /** The supports. The orders say "CRAWL UNDER IT AT THE MARKED SUPPORTS" and nothing marked them. */
  gaps: Array<[x0: number, x1: number]>;
}

/** A building the map draws the footprint of: Recon Photos, and nothing without it. */
export interface StructureMark {
  x: number;
  z: number;
  hx: number;
  hz: number;
}

/** The fixed layer's geometry, all of it, in mission metres. */
export interface BackdropPlan {
  water: WaterMark[];
  pipes: PipeMark[];
  /** Minefields whose tell is visible to everyone (`marked`); the rest are not the map's to give away. */
  mines: Rect[];
  /** The objective's own zones - NOT `goalZone`, which is the enemy's ground. */
  zones: Rect[];
  failCircles: MapCircle[];
  /** Recon Photos (04 §8): the buildings. Empty without the card. */
  structures: StructureMark[];
  /** Patrol Schedule (04 §8): each sentry round as a closed ring of waypoints. Empty without the card. */
  patrols: Array<Array<[x: number, z: number]>>;
  /** Inside Man (04 §8): the gate that stands open. Null without the card. */
  openGate: { x: number; z: number; width: number } | null;
}

/**
 * Where a barrier's course crosses this x. Its own copy of the walk, as `hazards/terrain.ts` keeps its
 * own distance function: the HUD does not import the generator that built the river.
 */
function courseZ(course: Polyline, x: number): number {
  const pts = course.points;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    if (!a || !b || x < a[0] || x > b[0]) continue;
    return b[0] === a[0] ? a[1] : a[1] + ((b[1] - a[1]) * (x - a[0])) / (b[0] - a[0]);
  }
  const edge = x < (pts[0]?.[0] ?? 0) ? pts[0] : pts[pts.length - 1];
  return edge?.[1] ?? 0;
}

const propAt = (def: MissionDef, id: number): { x: number; z: number } | undefined =>
  def.props.find((p) => p.id === id);

/** Below this a break between two lengths of pipe is a join, not a support: the same 0.5 m the generator asserts. */
const SUPPORT_GAP = 0.5;

/**
 * The pipe, run by run, with the gaps between its runs. There is no marker prop for a support - the
 * generator builds the pipe as lengths either side of each one (`buildRun`) - so the map works them out
 * the way the archetype's own assert does: sort the lengths along x, and every break wider than a join
 * is somewhere to crawl under (ROUND5 §2.6). Grouped by z, so a second pipe would get its own line.
 */
export function pipeMarks(def: MissionDef): PipeMark[] {
  const rows = new Map<number, Array<[number, number]>>();
  for (const p of def.props as PropDef[]) {
    if (p.kind !== 'pipeline') continue;
    const half = (p.length ?? 0) / 2;
    const row = rows.get(p.z) ?? [];
    row.push([p.x - half, p.x + half]);
    rows.set(p.z, row);
  }
  const out: PipeMark[] = [];
  for (const [z, spans] of rows) {
    spans.sort((a, b) => a[0] - b[0]);
    const runs: Array<[number, number]> = [];
    const gaps: Array<[number, number]> = [];
    for (const span of spans) {
      const last = runs[runs.length - 1];
      // Touching lengths are one run of pipe: only a real break is a way through.
      if (last !== undefined && span[0] - last[1] <= SUPPORT_GAP) last[1] = Math.max(last[1], span[1]);
      else {
        if (last !== undefined) gaps.push([last[1], span[0]]);
        runs.push([span[0], span[1]]);
      }
    }
    out.push({ z, runs, gaps });
  }
  return out;
}

/**
 * The circles a mission is lost inside: being SEEN within `notSeenNear`, and being caught by a strike
 * he called himself. Six archetypes carry one (20 m to 80 m) and none of them ever drew it (ROUND5 §1.5).
 */
export function failCircles(def: MissionDef): MapCircle[] {
  const out: MapCircle[] = [];
  const o = def.objective;
  const struck = new Set<number>();
  for (const rule of o.rules) {
    if (rule.type === 'notSeenNear') {
      const prop = propAt(def, rule.propId);
      if (prop) out.push({ x: prop.x, z: prop.z, r: rule.radius });
    } else if (rule.type === 'timedEvent' && rule.effect === 'strikeLands') {
      // The same default the sim uses: the named props, else the point he must be clear of, else the targets.
      const ids = rule.strikePropIds ?? (o.clearFromPropId === undefined ? o.targetPropIds : [o.clearFromPropId]);
      for (const id of ids) struck.add(id);
    }
  }
  if (o.clearDistance !== undefined) {
    if (o.clearFromPropId !== undefined) struck.add(o.clearFromPropId);
    for (const id of struck) {
      const prop = propAt(def, id);
      if (prop) out.push({ x: prop.x, z: prop.z, r: o.clearDistance });
    }
  }
  return out;
}

/**
 * What "the map's structures" are (04 §8, Recon Photos): a building with a roof. `PropSpec.shelter`
 * is the game's own flag for "somebody lives or works here" and is most of it; the fortifications
 * and the ruin are added by name because those are the first things a photograph shows and the
 * things he has to walk round. Guns, masts, radars and vehicles stay off: they are the garrison
 * and its kit rather than the shape of the place, and a map 44 px across has no room to say both.
 */
const EXTRA_STRUCTURES: readonly PropKind[] = ['bunker', 'magazine', 'ammoShack', 'casemate', 'ruinHouse', 'watchtower'];

const isStructure = (kind: PropKind): boolean =>
  PROPS[kind].shelter === true || EXTRA_STRUCTURES.includes(kind);

/**
 * The buildings, with the footprint the world actually gives them (ROUND5: the card said "reveals the
 * map's structures" and nothing read it at all). Taken from the colliders rather than a table of sizes,
 * so a hangar draws as a hangar and a tent as a tent, and a prop that is turned draws as it stands.
 * Pure, and empty for a mission flown without the card: this is bought knowledge, not free knowledge.
 */
export function structureMarks(def: MissionDef): StructureMark[] {
  if (!def.intel.includes('recon-photos')) return [];
  const out: StructureMark[] = [];
  for (const prop of def.props as PropDef[]) {
    if (!isStructure(prop.kind)) continue;
    let hx = 0;
    let hz = 0;
    for (const c of collidersFor(prop)) {
      hx = Math.max(hx, Math.abs(c.x - prop.x) + (c.shape === 'circle' ? c.r : c.hx));
      hz = Math.max(hz, Math.abs(c.z - prop.z) + (c.shape === 'circle' ? c.r : c.hz));
    }
    if (hx > 0 && hz > 0) out.push({ x: prop.x, z: prop.z, hx, hz });
  }
  return out;
}

/**
 * The patrol rounds (04 §8, Patrol Schedule): the card he paid 8 RP for, which drew nothing at all.
 *
 * `SpawnDef.patrol` is a LOOPING route, so the ring is closed - a line with two ends would read as a
 * path from somewhere to somewhere else, which is the opposite of what a patrol is. A sentry has no
 * route and gets no mark: he holds his post, and a ring round a man who never moves would be a lie.
 * A spawn that does not exist yet (`dormantUntil`) gets none either: a schedule cannot know the round
 * of a search party that has not been called out. Identical rounds are drawn once, because High Alert
 * doubles the patrols on to the same waypoints and the map would only paint the same ring twice.
 */
export function patrolRoutes(def: MissionDef): Array<Array<[x: number, z: number]>> {
  if (!def.intel.includes('patrol-schedule')) return [];
  const seen = new Set<string>();
  const out: Array<Array<[number, number]>> = [];
  for (const spawn of def.spawns) {
    const route = spawn.patrol;
    if (route === undefined || route.length < 2 || spawn.dormantUntil !== undefined) continue;
    const key = route.map(([x, z]) => `${x},${z}`).join(';');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push([...route.map(([x, z]): [number, number] => [x, z]), [route[0]?.[0] ?? 0, route[0]?.[1] ?? 0]]);
  }
  return out;
}

/** Everything the fixed layer draws beyond the roads, trenches, ponds and the goal zone. Pure. */
export function backdropPlan(def: MissionDef): BackdropPlan {
  const water = def.waterBarriers.map((b): WaterMark => ({
    course: b.course,
    surface: b.surface,
    crossings: b.crossings.map((x) => ({ x, z: courseZ(b.course, x), halfWidth: HAZARDS.crossingHalfWidth })),
    // Cracked ice only exists on ice; a `weakIce` list on open water would be a lie about the surface.
    weakIce: b.surface === 'ice' ? b.weakIce : [],
  }));
  const o = def.objective;
  return {
    water,
    pipes: pipeMarks(def),
    mines: def.mineFields.filter((f) => f.marked).map((f) => f.rect),
    zones: o.zone === undefined ? [...(o.zones ?? [])] : [o.zone, ...(o.zones ?? [])],
    failCircles: failCircles(def),
    structures: structureMarks(def),
    patrols: patrolRoutes(def),
    // The gate only exists on a mission flown with Inside Man, so no further gate on the card is needed.
    openGate: def.openGate === undefined ? null
      : { x: def.openGate.x, z: def.openGate.z, width: def.openGate.width },
  };
}
